import React from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import { format } from 'date-fns';
import { Unit, Event, Group } from '../../types';

const EMPTY = (msg: string) => (
  <div className="flex items-center justify-center h-full text-sm text-slate-400">{msg}</div>
);

// ── Battery distribution donut ──────────────────────────────
export const BatteryDonut: React.FC<{ units: Unit[] }> = ({ units }) => {
  const ok = units.filter((u) => (u.battery ?? 0) >= 50).length;
  const mid = units.filter((u) => (u.battery ?? 0) >= 20 && (u.battery ?? 0) < 50).length;
  const low = units.filter((u) => (u.battery ?? 0) < 20).length;
  const data = [
    { name: 'OK (≥50%)', value: ok, color: '#10b981' },
    { name: 'Medium (20–49%)', value: mid, color: '#f59e0b' },
    { name: 'Low (<20%)', value: low, color: '#ef4444' },
  ].filter((d) => d.value > 0);

  if (units.length === 0) return EMPTY('No locks yet');

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" paddingAngle={2}>
          {data.map((d) => (
            <Cell key={d.name} fill={d.color} stroke="none" />
          ))}
        </Pie>
        <Tooltip />
        <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

// ── Locks per group bars ────────────────────────────────────
export const LocksPerGroupBars: React.FC<{ groups: Group[] }> = ({ groups }) => {
  const data = groups
    .map((g) => ({ name: g.name, locks: g.units?.length || 0 }))
    .sort((a, b) => b.locks - a.locks)
    .slice(0, 8);

  if (groups.length === 0) return EMPTY('No groups yet');

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} interval={0} angle={-20} textAnchor="end" height={50} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
        <Tooltip cursor={{ fill: '#f8fafc' }} />
        <Bar dataKey="locks" fill="#6366f1" radius={[6, 6, 0, 0]} maxBarSize={48} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ── Events over time (per day) ──────────────────────────────
export const EventsOverTime: React.FC<{ events: Event[] }> = ({ events }) => {
  if (events.length === 0) return EMPTY('No events to chart');

  const counts = new Map<string, number>();
  for (const e of events) {
    const d = new Date(e.registered_at);
    if (Number.isNaN(d.getTime())) continue;
    const key = format(d, 'yyyy-MM-dd');
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const data = Array.from(counts.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([day, value]) => ({ day: format(new Date(day), 'dd/MM'), value }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
        <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} minTickGap={24} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
        <Tooltip />
        <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
};
